<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h1 class="h4 mb-0">Achats ({{ totalRows }})</h1>

        <div class="d-flex gap-2">
          <ModuleActivityButton :module="['purchases', 'Purchase', 'PurchaseItem']" label="Achats" />

          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            :class="{ active: filtersVisible }"
            :title="filtersVisible ? 'Masquer les filtres' : 'Afficher les filtres'"
            @click="toggleFilters"
          >
            <i class="fa" :class="filtersVisible ? 'fa-eye-slash' : 'fa-filter'"></i>
            <span class="btn-label ms-1">Filtres</span>
          </button>

          <button class="btn btn-primary btn-sm btn-icon-label" type="button" @click="openCreateDialog">
            <i class="fa fa-plus"></i>
            <span class="btn-label ms-1">Nouvel achat</span>
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger mb-3 py-2">
        {{ errorMessage }}
      </div>
      <div v-if="successMessage" class="alert alert-success mb-3 py-2">
        {{ successMessage }}
      </div>

      <!-- ✅ FILTRES -->
      <div v-show="filtersVisible" class="card module-filter-card mb-3">
        <div class="card-body py-2">
          <div class="row g-2 align-items-end">
            <!-- Période -->
            <div class="col-md-3">
              <label class="form-label small mb-1">Du</label>
              <input type="date" class="form-control form-control-sm" v-model="filters.date_from" />
            </div>
            <div class="col-md-3">
              <label class="form-label small mb-1">Au</label>
              <input type="date" class="form-control form-control-sm" v-model="filters.date_to" />
            </div>

            <!-- Fournisseur -->
            <div class="col-md-3">
              <label class="form-label small mb-1">Fournisseur</label>
              <select class="form-select form-select-sm" v-model="filters.supplier_id">
                <option :value="''">Tous</option>
                <option v-for="s in suppliers" :key="s.id" :value="s.id">
                  {{ s.name }}
                </option>
              </select>
            </div>

            <!-- Caissier / Créé par -->
            <div class="col-md-3">
              <label class="form-label small mb-1">Créé par</label>
              <select class="form-select form-select-sm" v-model="filters.created_by">
                <option :value="''">Tous</option>
                <option v-for="u in cashiers" :key="u.id" :value="u.id">
                  {{ u.name }}
                </option>
              </select>
            </div>

            <div class="col-md-3">
              <label class="form-label small mb-1">Statut</label>
              <select class="form-select form-select-sm" v-model="filters.status">
                <option value="">Tous</option>
                <option value="draft">Brouillon</option>
                <option value="validated">Validé</option>
              </select>
            </div>

            <div class="col-12 module-filter-actions mt-2">
              <button type="button" class="btn btn-outline-secondary btn-sm btn-icon-label" @click="resetFilters">
                <i class="fa fa-rotate-left"></i>
                <span class="btn-label ms-1">Réinitialiser</span>
              </button>
              <button type="button" class="btn btn-primary btn-sm btn-icon-label" @click="fetchPurchases">
                <i class="fa fa-search"></i>
                <span class="btn-label ms-1">Filtrer</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <BootstrapDataTable
        :response="purchasesResponse"
        :columns="tableColumns"
        :loading="isLoading"
        :selectable="canBulkDeletePurchases"
        v-model:selected-row-ids="bulkSelectedIds"
        :is-row-selectable="canSelectPurchaseForBulkDelete"
        :selection-disabled="bulkActionPending"
        :sort-by="table.sortBy"
        :sort-dir="table.sortDir"
        :per-page="table.perPage"
        :active-filter-count="activeFilterCount"
        state-key="purchases.list"
        searchable
        v-model:search-value="filters.search"
        search-placeholder="Rechercher référence ou fournisseur..."
        detail-route-name="PurchaseShow"
        empty-text="Aucun achat trouvé."
        @search-change="handleTableSearch"
        @sort-change="handleSortChange"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
        @state-restored="Object.assign(table, { page: $event.page, perPage: $event.perPage })"
        @refresh="fetchPurchases"
      >
        <template #bulk-actions="{ selectedCount, isOverLimit }">
          <button
            type="button"
            class="btn btn-sm btn-outline-danger"
            :disabled="bulkActionPending || isOverLimit"
            @click="bulkDeletePurchases"
          >
            <i class="fa fa-trash me-1" aria-hidden="true"></i>Supprimer {{ selectedCount }}
          </button>
        </template>

        <template #cell-created_by_name="{ row }">
          {{ getCreatedByName(row) }}
        </template>

        <template #cell-status="{ row }">
          <span class="badge" :class="statusClass(row.status)">
            {{ statusLabel(row.status) }}
          </span>
        </template>

        <template #cell-total_amount="{ row }">
          {{ $format.currency(row.total_amount) }}
        </template>

        <template #cell-purchase_date="{ row }">
          {{ $format.date(row.purchase_date) }}
        </template>
      </BootstrapDataTable>

      <!-- Dialog création rapide -->
      <PrimevueDialog
        v-model:visible="headerDialogVisible"
        :modal="true"
        :style="{ width: '520px', maxWidth: '95vw' }"
        :closable="false"
      >
        <template #header>
          <h5 class="mb-0">Nouvelle commande d'achat</h5>
        </template>

        <form @submit.prevent="handleHeaderSave">
          <div class="mb-3">
            <label class="form-label small fw-semibold">Fournisseur *</label>
            <select v-model.number="purchaseHeaderForm.supplier_id" class="form-select form-select-sm" required>
              <option :value="null" disabled>Sélectionner un fournisseur</option>
              <option v-for="supplier in suppliers" :key="supplier.id" :value="supplier.id">
                {{ supplier.name }}
              </option>
            </select>
          </div>

          <div class="mb-3">
            <label class="form-label small fw-semibold">Référence commande</label>
            <input
              v-model="purchaseHeaderForm.reference"
              type="text"
              class="form-control form-control-sm"
              placeholder="Laisser vide pour auto-générer"
            />
          </div>

          <div class="mb-3">
            <label class="form-label small fw-semibold">Date d'achat *</label>
            <input v-model="purchaseHeaderForm.purchase_date" type="date" class="form-control form-control-sm" required />
          </div>

          <div class="mb-0">
            <label class="form-label small fw-semibold">Commentaire</label>
            <textarea
              v-model="purchaseHeaderForm.comment"
              class="form-control form-control-sm"
              rows="3"
              placeholder="Commentaire facultatif"
            ></textarea>
          </div>

          <div class="d-flex justify-content-end gap-2 mt-4">
            <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isSavingHeader" @click="closeHeaderDialog">
            <i class="fa fa-xmark me-1" aria-hidden="true"></i>
            Annuler
            </button>
            <AppLoadingButton
              type="submit"
              class="btn btn-primary btn-sm"
              :loading="isSavingHeader"
              loading-label="Enregistrement..."
            >
              <i class="fa fa-save me-1"></i>
              Enregistrer
            </AppLoadingButton>
          </div>
        </form>
      </PrimevueDialog>

      <!-- Dialog édition complète -->
      <PrimevueDialog
        v-model:visible="formDialogVisible"
        :modal="true"
        :style="{ width: '80vw', maxWidth: '95vw' }"
        :breakpoints="{ '768px': '95vw' }"
        :closable="false"
      >
        <template #header>
          <div class="d-flex align-items-center justify-content-between gap-2 w-100">
            <h5 class="mb-0">
              Modifier l'achat
              <span v-if="selectedPurchase?.reference" class="text-muted fw-normal ms-1">
                #{{ selectedPurchase.reference }}
              </span>
            </h5>
            <button
              type="button"
              class="btn btn-outline-primary btn-sm btn-action-icon"
              title="Ouvrir la modification dans une page"
              aria-label="Ouvrir la modification dans une page"
              :disabled="!selectedPurchase?.id"
              @click="openSelectedPurchaseEditPage"
            >
              <i class="fa fa-up-right-from-square"></i>
            </button>
          </div>
        </template>

        <PurchaseForm
          v-if="formDialogMode === 'edit'"
          :mode="formDialogMode"
          :purchase="selectedPurchase"
          @save="handleSave"
          @cancel="closeFormDialog"
          @suspend="handlePurchaseSuspend"
          @finish="handlePurchaseFinish"
        />
      </PrimevueDialog>

      <SupplierPriceChangesDialog
        v-model:visible="supplierPriceChangesDialogVisible"
        :changes="supplierPriceChanges"
        :saving="isValidating"
        @confirm="confirmValidationWithSupplierPrices"
      />
    </template>
  </AdminLayout>
</template>

<script>
import PurchaseForm from './PurchaseForm.vue';
import SupplierPriceChangesDialog from './SupplierPriceChangesDialog.vue';
import { bulkActionViewMixin } from '@/support/bulkActions';
import { hasPermission } from '@/support/permissions';
import {
  cleanFilterParams,
  countActiveFilters,
  extractApiTotal,
  loadModuleFilterState,
  saveModuleFilterValues,
  saveModuleFilterVisibility,
} from '@/support/moduleFilters';

const FILTER_MODULE = 'purchases';
const DEFAULT_FILTERS = {
  search: '',
  date_from: '',
  date_to: '',
  supplier_id: '',
  created_by: '',
  status: '',
};

export default {
  name: 'PurchasesView',
  mixins: [bulkActionViewMixin],
  components: {
    PurchaseForm,
    SupplierPriceChangesDialog,
  },
  data() {
    return {
      purchasesResponse: null,
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      table: {
        page: 1,
        perPage: 10,
        sortBy: 'purchase_date',
        sortDir: 'desc',
      },
      tableColumns: [
        { key: 'reference', label: 'Référence' },
        { key: 'supplier_name', label: 'Fournisseur' },
        { key: 'created_by_name', label: 'Créé par' },
        { key: 'status', label: 'Statut', sortable: true, class: 'text-center' },
        { key: 'total_amount', label: 'Montant', sortable: true, class: 'text-end' },
        { key: 'purchase_date', label: 'Date', sortable: true },
      ],

      // ✅ filtres
      filters: { ...DEFAULT_FILTERS },
      filtersVisible: false,

      // ✅ listes filtres
      suppliers: [],
      cashiers: [],

      formDialogVisible: false,
      formDialogMode: 'create', // 'create' | 'edit'
      selectedPurchase: null,
      pendingValidationPurchase: null,
      supplierPriceChangesDialogVisible: false,
      supplierPriceChanges: [],
      isValidating: false,
      headerDialogVisible: false,
      isSavingHeader: false,
      purchaseHeaderForm: {
        reference: '',
        supplier_id: null,
        purchase_date: '',
        comment: '',
      },
    };
  },
  mounted() {
    this.restoreFilters();
    this.initFilters();
    this.fetchPurchases();
  },
  computed: {
    currentUser() {
      return this.$store?.getters?.['auth/currentUser']
        || this.$store?.getters?.['auth/user']
        || this.$store?.state?.auth?.user
        || null;
    },
    canBulkDeletePurchases() {
      return hasPermission(this.currentUser, 'purchases.delete');
    },
    totalRows() {
      const fallback = Array.isArray(this.purchasesResponse?.data)
        ? this.purchasesResponse.data.length
        : 0;
      return extractApiTotal(this.purchasesResponse, fallback);
    },
    activeFilterCount() {
      return countActiveFilters(this.filters, DEFAULT_FILTERS, ['search']);
    },
  },
  watch: {
    filters: {
      deep: true,
      handler(filters) {
        saveModuleFilterValues(FILTER_MODULE, filters);
      },
    },
    filtersVisible(value) {
      saveModuleFilterVisibility(FILTER_MODULE, value);
    },
  },
  methods: {
    canSelectPurchaseForBulkDelete(purchase) {
      return purchase?.status === 'draft';
    },

    async bulkDeletePurchases() {
      await this.performBulkAction({
        url: '/purchases/bulk',
        method: 'delete',
        itemLabel: 'achat',
        confirmation: (count) => ({
          header: 'Supprimer les achats',
          message: `Supprimer les ${count} achats brouillon sélectionnés ?`,
          acceptLabel: `Supprimer ${count}`,
          acceptClass: 'p-button-danger',
        }),
        refresh: () => this.fetchPurchases(),
      });
    },

    restoreFilters() {
      const state = loadModuleFilterState(FILTER_MODULE, DEFAULT_FILTERS);
      this.filters = state.values;
      this.filtersVisible = state.visible;
    },

    toggleFilters() {
      this.filtersVisible = !this.filtersVisible;
    },

    async initFilters() {
      // on charge fournisseurs + utilisateurs (caissiers)
      this.fetchSuppliers();
      this.fetchCashiers();
    },

    async fetchSuppliers() {
      try {
        const res = await this.$api.get('/suppliers', { params: { limit: 1000 } });
        this.suppliers = res.data.data || res.data || [];
      } catch (e) {
        console.error('Erreur chargement fournisseurs (filtres)', e);
        this.suppliers = [];
      }
    },

    async fetchCashiers() {
      try {
        // ✅ adapte si ton endpoint est différent
        // option: params.role=caissier
        const res = await this.$api.get('/users', { params: { limit: 1000 } });
        const users = res.data.data || res.data || [];

        // si tu as role dans l'user, on filtre caissier+gerant (au choix)
        this.cashiers = (users || []).filter((u) => u.role === 'caissier' || u.role === 'gerant');
      } catch (e) {
        console.error('Erreur chargement utilisateurs (filtres)', e);
        this.cashiers = [];
      }
    },

    buildParamsFromFilters() {
      return {
        ...cleanFilterParams(this.filters),
        paginate: 1,
        page: this.table.page,
        per_page: this.table.perPage,
        sort_by: this.table.sortBy,
        sort_dir: this.table.sortDir,
      };
    },

    async fetchPurchases() {
      this.isLoading = true;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        const params = this.buildParamsFromFilters();
        const res = await this.$api.get('/purchases', { params });
        this.purchasesResponse = res.data;
      } catch (e) {
        console.error('Erreur chargement achats', e);
        this.errorMessage = 'Impossible de charger les achats.';
      } finally {
        this.isLoading = false;
      }
    },

    resetFilters() {
      this.filters = { ...DEFAULT_FILTERS };
      this.table.page = 1;
      this.fetchPurchases();
    },

    handleSortChange({ sortBy, sortDir }) {
      this.table.sortBy = sortBy;
      this.table.sortDir = sortDir;
      this.table.page = 1;
      this.fetchPurchases();
    },

    handlePageChange(page) {
      this.table.page = page;
      this.fetchPurchases();
    },

    handlePerPageChange(perPage) {
      this.table.perPage = perPage;
      this.table.page = 1;
      this.fetchPurchases();
    },

    handleTableSearch(search) {
      this.filters.search = search;
      this.table.page = 1;
      this.fetchPurchases();
    },

    // ✅ affichage "créé par" (fallback intelligent)
    getCreatedByName(p) {
      // cas 1: backend renvoie directement un champ
      if (p.created_by_name) return p.created_by_name;

      // cas 2: relation
      if (p.created_by_user && p.created_by_user.name) return p.created_by_user.name;

      // cas 3: id => lookup dans cashiers
      if (p.created_by) {
        const u = this.cashiers.find((x) => x.id === p.created_by);
        if (u) return u.name;
      }

      return '-';
    },

    statusLabel(status) {
      return status === 'validated' ? 'Validé' : 'Brouillon';
    },

    statusClass(status) {
      return status === 'validated' ? 'bg-success' : 'bg-secondary';
    },

    openCreateDialog() {
      const today = new Date().toISOString().slice(0, 10);
      this.purchaseHeaderForm = {
        reference: '',
        supplier_id: null,
        purchase_date: today,
        comment: '',
      };
      this.errorMessage = '';
      this.successMessage = '';
      this.headerDialogVisible = true;
      this.fetchSuppliers();
    },

    closeHeaderDialog() {
      this.headerDialogVisible = false;
    },

    closeFormDialog() {
      this.formDialogVisible = false;
      this.selectedPurchase = null;
    },

    async handlePurchaseSuspend() {
      this.formDialogVisible = false;
      this.selectedPurchase = null;
      await this.fetchPurchases();
      this.successMessage = 'Achat suspendu en brouillon.';
    },

    async handlePurchaseFinish(purchase) {
      const purchaseToValidate = purchase || this.selectedPurchase;
      if (!purchaseToValidate) return;

      this.formDialogVisible = false;
      this.selectedPurchase = purchaseToValidate;
      await this.fetchPurchases();
      await this.validatePurchase(purchaseToValidate);
    },

    openSelectedPurchaseEditPage() {
      if (!this.selectedPurchase?.id) return;

      this.formDialogVisible = false;
      this.$router.push({ name: 'PurchaseEdit', params: { id: this.selectedPurchase.id } });
    },

    async handleSave(payload) {
      try {
        if (this.formDialogMode === 'create') {
          await this.$api.post('/purchases', payload);
          this.successMessage = 'Achat créé avec succès.';
        } else {
          const id = payload.id || (this.selectedPurchase && this.selectedPurchase.id);
          await this.$api.put(`/purchases/${id}`, payload);
          this.successMessage = 'Achat modifié avec succès.';
        }
        this.formDialogVisible = false;
        await this.fetchPurchases();
      } catch (error) {
        console.error('Erreur sauvegarde achat', error);
        let msg = "Une erreur est survenue lors de l'enregistrement de l'achat.";
        if (error?.response?.data?.errors) {
          const rawErrors = Object.values(error.response.data.errors);
          const flatMessages = rawErrors.reduce((acc, val) => acc.concat(val), []);
          msg = flatMessages.join(' ');
        }
        this.errorMessage = msg;
      }
    },

    async handleHeaderSave() {
      if (this.isSavingHeader) return;

      if (!this.purchaseHeaderForm.supplier_id) {
        this.errorMessage = 'Merci de sélectionner un fournisseur.';
        return;
      }

      this.isSavingHeader = true;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        const response = await this.$api.post('/purchases', {
          reference: this.purchaseHeaderForm.reference || null,
          supplier_id: this.purchaseHeaderForm.supplier_id,
          purchase_date: this.purchaseHeaderForm.purchase_date,
          comment: this.purchaseHeaderForm.comment || null,
        });

        const purchaseId = response.data?.data?.id;
        this.headerDialogVisible = false;

        if (purchaseId) {
          await this.$router.push({ name: 'PurchaseEdit', params: { id: purchaseId } });
          return;
        }

        await this.fetchPurchases();
        this.successMessage = "Commande d'achat créée en brouillon.";
      } catch (error) {
        console.error('Erreur création commande achat', error);
        let msg = "Une erreur est survenue lors de la création de la commande d'achat.";
        if (error?.response?.data?.errors) {
          const rawErrors = Object.values(error.response.data.errors);
          const flatMessages = rawErrors.reduce((acc, val) => acc.concat(val), []);
          msg = flatMessages.join(' ');
        }
        this.errorMessage = msg;
      } finally {
        this.isSavingHeader = false;
      }
    },

    async validatePurchase(row) {
      if (!row || row.status !== 'draft') return;

      this.errorMessage = '';
      this.successMessage = '';
      this.pendingValidationPurchase = row;

      try {
        const response = await this.$api.get(
          `/purchases/${row.id}/supplier-price-changes`
        );
        const changes = response.data?.data?.changes || [];

        if (changes.length > 0) {
          this.supplierPriceChanges = changes;
          this.supplierPriceChangesDialogVisible = true;
          return;
        }
      } catch (error) {
        console.error('Erreur prévisualisation prix fournisseur', error);
        this.errorMessage = this.extractErrorMessage(
          error,
          'Impossible de vérifier les prix fournisseur avant validation.'
        );
        return;
      }

      const ok = await this.$confirmAction(
        `Valider l'achat "${row.reference}" ? Les quantités seront prises en compte dans le stock (logique backend).`
      );
      if (!ok) return;

      await this.submitValidation(row, false, []);
    },

    async confirmValidationWithSupplierPrices(payload) {
      if (!this.pendingValidationPurchase) return;

      await this.submitValidation(
        this.pendingValidationPurchase,
        payload.updateSupplierPrices,
        payload.productIds
      );
    },

    async submitValidation(row, updateSupplierPrices, productIds) {
      this.isValidating = true;

      try {
        await this.$api.post(`/purchases/${row.id}/validate`, {
          update_supplier_prices: updateSupplierPrices,
          supplier_price_product_ids: productIds || [],
        });
        this.supplierPriceChangesDialogVisible = false;
        this.supplierPriceChanges = [];
        this.pendingValidationPurchase = null;
        this.successMessage = 'Achat validé avec succès.';
        await this.fetchPurchases();
      } catch (error) {
        console.error('Erreur validation achat', error);
        this.errorMessage = this.extractErrorMessage(
          error,
          "Une erreur est survenue lors de la validation de l'achat."
        );
      } finally {
        this.isValidating = false;
      }
    },

    async deletePurchase(row) {
      const ok = await this.$confirmAction(`Supprimer l'achat "${row.reference}" ?`);
      if (!ok) return;

      this.errorMessage = '';
      this.successMessage = '';

      try {
        await this.$api.delete(`/purchases/${row.id}`);
        this.successMessage = 'Achat supprimé.';
        await this.fetchPurchases();
      } catch (error) {
        console.error('Erreur suppression achat', error);
        let msg = "Une erreur est survenue lors de la suppression de l'achat.";
        if (error?.response?.data?.errors) {
          const rawErrors = Object.values(error.response.data.errors);
          const flatMessages = rawErrors.reduce((acc, val) => acc.concat(val), []);
          msg = flatMessages.join(' ');
        }
        this.errorMessage = msg;
      }
    },

    openEditDialog(row) {
      if (!row?.id) return;

      this.$router.push({ name: 'PurchaseEdit', params: { id: row.id } });
    },

    async loadPurchaseDetail(id) {
      try {
        const res = await this.$api.get(`/purchases/${id}`);
        this.selectedPurchase = res.data.data; // contient purchase + items
        this.formDialogVisible = true;
      } catch (e) {
        console.error('Erreur chargement détails achat', e);
        this.errorMessage = "Impossible de charger les détails de l'achat.";
      }
    },

    extractErrorMessage(error, fallback) {
      if (error?.response?.data?.errors) {
        return Object.values(error.response.data.errors).flat().join(' ');
      }

      return fallback;
    },
  },
};
</script>
